import React, { ReactElement, useEffect } from 'react';
import { QAlert, QDivider, QHeading } from '@qualio/ui-components';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useTranslation } from 'react-i18next';
import { QStackContainer } from '../../../../styles/Users.styles';
import { isEmpty } from '../../../../utils/object';
import { CommonProps } from '../../types';
import ReassignOwnerSummaryItem from './ReassignOwnerSummaryItem';
import StandardSummaryItem from './StandardSummaryItem';
import { EntitiesState } from '../../state';
import { ReassignOwnerSummaryItemType } from '../types';
import ReassignEventTemplateSummaryItem from './ReassignEventTemplateSummaryItem';

type SummaryProps = Pick<CommonProps, 'setShowNextButton'> & {
  title: string;
  entityState: EntitiesState;
};

const Summary = ({ title, setShowNextButton, entityState }: SummaryProps): ReactElement => {
  const { t } = useTranslation();
  useEffect(() => {
    setShowNextButton(false);

    return () => setShowNextButton(true);
  }, []);

  const { changeManagementDocTemplate, eventCollaborators } = useFlags();

  const {
    owner: ownerItems,
    documentTemplateOwner: documentTemplateOwnerItems,
    changeRequestOwner: changeRequestOwnerItems,
    approver: approverItems,
    reviewer: reviewerItems,
    issues: issueItems,
    tasks: taskItems,
    default_owner: defaultOwnerItems,
    eventTemplateOwner: eventTemplateOwnerItems,
    controls: controlItems,
  } = entityState;

  return (
    <QStackContainer direction="column" spacing={3}>
      <QHeading size="md">{title}</QHeading>
      <QAlert description={t('reassignEntities.reviewInfo')} status="info" />
      {!isEmpty(ownerItems) && (
        <>
          <ReassignOwnerSummaryItem items={ownerItems} type={ReassignOwnerSummaryItemType.document} />
          <QDivider />
        </>
      )}
      {changeManagementDocTemplate && !isEmpty(documentTemplateOwnerItems) && (
        <>
          <ReassignOwnerSummaryItem
            items={documentTemplateOwnerItems}
            type={ReassignOwnerSummaryItemType.documentTemplate}
          />
          <QDivider />
        </>
      )}
      {!isEmpty(changeRequestOwnerItems) && (
        <>
          <ReassignOwnerSummaryItem items={changeRequestOwnerItems} type={ReassignOwnerSummaryItemType.changeRequest} />
          <QDivider />
        </>
      )}
      {!isEmpty(approverItems) && (
        <>
          <StandardSummaryItem items={approverItems} summaryCategory="approver" />
          <QDivider />
        </>
      )}

      {!isEmpty(reviewerItems) && (
        <>
          <StandardSummaryItem items={reviewerItems} summaryCategory="reviewer" />
          <QDivider />
        </>
      )}

      {!isEmpty(issueItems) && (
        <>
          <StandardSummaryItem
            items={issueItems}
            summaryCategory="issues"
            note={eventCollaborators ? t('reassignEntities.summary.issueCollaboratorNote') : undefined}
          />
          <QDivider />
        </>
      )}
      {!isEmpty(taskItems) && (
        <>
          <StandardSummaryItem items={taskItems} summaryCategory="tasks" />
          <QDivider />
        </>
      )}
      {(!isEmpty(defaultOwnerItems) || !isEmpty(eventTemplateOwnerItems)) && (
        <>
          <ReassignEventTemplateSummaryItem
            defaultAssigneeItems={defaultOwnerItems}
            ownerItems={eventTemplateOwnerItems}
          />
          <QDivider />
        </>
      )}
      {!isEmpty(controlItems) && (
        <>
          <ReassignOwnerSummaryItem items={controlItems} type={ReassignOwnerSummaryItemType.control} />
          <QDivider />
        </>
      )}
    </QStackContainer>
  );
};

export default Summary;
